// src/components/common/ReportDatePresetFilter/ReportDatePresetFilter.jsx
import React from 'react';
import DateRangePicker from '../../ui/DateRangePicker/DateRangePicker';
import styles from './ReportDatePresetFilter.module.css';

const today = () => {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return d;
};

const toYMD = (d) => {
  const year = d.getFullYear();
  const month = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const DEFAULT_PRESET_KEYS = ['today', 'yesterday', 'last7Days', 'last30Days', 'thisMonth', 'lastMonth', 'last90Days'];

export const getPresets = () => {
  const t = today();
  const yesterday = new Date(t); yesterday.setDate(t.getDate() - 1);
  const weekStart = new Date(t); weekStart.setDate(t.getDate() - ((t.getDay() + 6) % 7));
  const yearStart = new Date(t.getFullYear(), 0, 1);
  const last7Start = new Date(t); last7Start.setDate(t.getDate() - 6);
  const last30Start = new Date(t); last30Start.setDate(t.getDate() - 29);
  const last90Start = new Date(t); last90Start.setDate(t.getDate() - 89);
  const thisMonthStart = new Date(t.getFullYear(), t.getMonth(), 1);
  const lastMonthStart = new Date(t.getFullYear(), t.getMonth() - 1, 1);
  const lastMonthEnd = new Date(t.getFullYear(), t.getMonth(), 0);

  return [
    { key: 'today', label: 'Today', from: toYMD(t), to: toYMD(t) },
    { key: 'yesterday', label: 'Yesterday', from: toYMD(yesterday), to: toYMD(yesterday) },
    { key: 'last7Days', label: 'Last 7 Days', from: toYMD(last7Start), to: toYMD(t) },
    { key: 'last30Days', label: 'Last 30 Days', from: toYMD(last30Start), to: toYMD(t) },
    { key: 'thisMonth', label: 'This Month', from: toYMD(thisMonthStart), to: toYMD(t) },
    { key: 'lastMonth', label: 'Last Month', from: toYMD(lastMonthStart), to: toYMD(lastMonthEnd) },
    { key: 'last90Days', label: 'Last 90 Days', from: toYMD(last90Start), to: toYMD(t) },
    { key: 'thisWeek', label: 'This Week', from: toYMD(weekStart), to: toYMD(t) },
    { key: 'thisYear', label: 'This Year', from: toYMD(yearStart), to: toYMD(t) },
  ];
};

/**
 * ReportDatePresetFilter
 *
 * A row of always-visible quick-range preset buttons (Today, Yesterday, Last 7 Days,
 * Last 30 Days, This Month, Last Month, Last 90 Days), followed by the existing
 * DateRangePicker for picking an exact custom range.
 *
 * @param {Object} props
 * @param {Object} props.value - { dateFrom: 'YYYY-MM-DD', dateTo: 'YYYY-MM-DD' }
 * @param {function} props.onChange - ({ dateFrom, dateTo }) => void
 * @param {string[]} [props.presets] - preset keys to show, in order
 * @param {React.ReactNode} [props.extra] - extra control rendered beside the range picker
 */
const ReportDatePresetFilter = ({ value = {}, onChange, presets: presetKeys = DEFAULT_PRESET_KEYS, extra = null }) => {
  const allPresets = getPresets();
  const presets = presetKeys.map((key) => allPresets.find((p) => p.key === key)).filter(Boolean);
  const activePreset = presets.find((p) => p.from === value.dateFrom && p.to === value.dateTo);

  const handlePresetClick = (preset) => {
    onChange({ dateFrom: preset.from, dateTo: preset.to });
  };

  const handleRangeChange = ({ from, to }) => {
    onChange({ dateFrom: from, dateTo: to });
  };

  return (
    <div className={styles.wrapper}>
      <div className={styles.presetRow}>
        {presets.map((preset) => (
          <button
            key={preset.key}
            type="button"
            className={`${styles.presetButton} ${activePreset?.key === preset.key ? styles.presetActive : ''}`}
            onClick={() => handlePresetClick(preset)}
          >
            {preset.label}
          </button>
        ))}
      </div>
      <div className={styles.rangePickerWrapper}>
        <DateRangePicker
          value={{ from: value.dateFrom || '', to: value.dateTo || '' }}
          onChange={handleRangeChange}
          placeholder="Custom Range"
        />
      </div>
      {extra && <div className={styles.extraWrapper}>{extra}</div>}
    </div>
  );
};

export default ReportDatePresetFilter;
